import { useState, useEffect, useCallback } from 'react'
import { api, ApiError } from '../../services/api'
import {
  PageHeader,
  DataTable,
  Alert,
  Modal,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { ScheduledJobEditModal } from './ScheduledJobEditModal'
import { useTableColumns } from '../../hooks/use-table-columns'

interface ScheduledJobRow {
  readonly id: string
  readonly organizationId: string | null
  readonly name: string
  readonly kind: string
  readonly cron: string
  readonly enabled: boolean
  readonly lastRunAt: string | null
  readonly nextRunAt: string | null
}

interface JobRun {
  readonly id: string
  readonly startedAt: string
  readonly completedAt: string | null
  readonly status: string
  readonly error: string | null
  readonly stats: Record<string, unknown> | null
}

function formatTs(ts: string | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleString()
}

export function ScheduledJobsPage() {
  const [rows, setRows] = useState<readonly ScheduledJobRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('scheduled-jobs')

  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyRows, setHistoryRows] = useState<readonly JobRun[]>([])
  const [historyJobName, setHistoryJobName] = useState<string>('')

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<{ rows: ScheduledJobRow[] }>('/admin/scheduled-jobs')
      setRows(res.data?.rows ?? [])
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load scheduled jobs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function runNow(job: ScheduledJobRow) {
    setError(null)
    try {
      await api.post(`/admin/scheduled-jobs/${job.id}/run-now`, {})
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Run-now failed')
    }
  }

  async function openHistory(job: ScheduledJobRow) {
    setHistoryJobName(job.name)
    try {
      const res = await api.get<{ rows: JobRun[] }>(`/admin/scheduled-jobs/${job.id}/runs?limit=50`)
      setHistoryRows(res.data?.rows ?? [])
      setHistoryOpen(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load run history')
    }
  }

  async function remove(job: ScheduledJobRow) {
    if (!window.confirm(`Delete scheduled job "${job.name}"? Run history will be removed.`)) return
    try {
      await api.delete(`/admin/scheduled-jobs/${job.id}`)
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed')
    }
  }

  const columns: readonly Column<ScheduledJobRow>[] = [
    { key: 'name', header: 'Name', sortable: true, render: (r) => <strong>{r.name}</strong> },
    { key: 'kind', header: 'Kind', sortable: true, render: (r) => r.kind },
    { key: 'cron', header: 'Cron', render: (r) => <code>{r.cron}</code> },
    { key: 'enabled', header: 'Enabled', render: (r) => (r.enabled ? 'Yes' : 'No') },
    { key: 'lastRunAt', header: 'Last run', render: (r) => formatTs(r.lastRunAt) },
    { key: 'nextRunAt', header: 'Next run', render: (r) => formatTs(r.nextRunAt) },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            style={{ ...btnSecondary, fontSize: '0.75rem', padding: '2px 8px' }}
            onClick={(e) => {
              e.stopPropagation()
              void runNow(r)
            }}
          >
            Run now
          </button>
          <button
            type="button"
            style={{ ...btnSecondary, fontSize: '0.75rem', padding: '2px 8px' }}
            onClick={(e) => {
              e.stopPropagation()
              void openHistory(r)
            }}
          >
            History
          </button>
          <button
            type="button"
            style={{
              ...btnSecondary,
              fontSize: '0.75rem',
              padding: '2px 8px',
              color: colors.danger,
            }}
            onClick={(e) => {
              e.stopPropagation()
              void remove(r)
            }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ]

  function openNewModal() {
    setEditingId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  function openEditModal(row: ScheduledJobRow) {
    setEditingId(row.id)
    setIsNew(false)
    setModalOpen(true)
  }

  return (
    <div>
      <PageHeader
        title="Scheduled Jobs"
        subtitle={`${rows.length} configured`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              New Job
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows as ScheduledJobRow[]}
          keyField="id"
          onRowClick={openEditModal}
          emptyMessage="No scheduled jobs configured. Create one to register a recurring task."
          {...tableColumnProps}
        />
      )}

      <ScheduledJobEditModal
        jobId={editingId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false)
          void refresh()
        }}
      />

      <Modal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`Run history — ${historyJobName}`}
        width="720px"
      >
        {historyRows.length === 0 ? (
          <p style={{ color: colors.textMuted }}>No runs yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', borderBottom: `1px solid ${colors.border}`, padding: '4px 8px' }}>
                  Started
                </th>
                <th style={{ textAlign: 'left', borderBottom: `1px solid ${colors.border}`, padding: '4px 8px' }}>
                  Status
                </th>
                <th style={{ textAlign: 'left', borderBottom: `1px solid ${colors.border}`, padding: '4px 8px' }}>
                  Stats / Error
                </th>
              </tr>
            </thead>
            <tbody>
              {historyRows.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: '4px 8px' }}>{formatTs(r.startedAt)}</td>
                  <td style={{ padding: '4px 8px' }}>{r.status}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {r.error ?? JSON.stringify(r.stats ?? {})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  )
}
